import { getResponseDiagnostics, getSignOutUrl, IAuthenticatedUserInfo, logAndBuildError } from '@notes/webintegrations-common'
import {
  IO365ShellShim,
  NavBarData,
  NavBarLinkData,
  ShellLoadErrorEvent,
} from '@suiteux/suiteux-shell'
import { isInternalUser } from '../lib/isInternalUser'
import { LocalizationService } from '../localization/LocalizationService'
import { getForceSignInUrl } from '../shared/auth/getSignInUrl'
import { Constants } from '../shared/Constants'
import { Errors } from '../shared/logger/Errors'
import { Log } from '../shared/logger/Log'
import { addScriptToBody } from './addScriptToBody'

/**
 * The shell code is really broken down into 3 parts. We want to separate this
 * from the core code and just call applyShell
 *
 * When we call applyShell, we need to
 * 1. retrieve the shell urls (client side resources + data)
 * 2. build out the shell object on the client
 * 3. render out the shell (attach on any additional customized settings)
 */

const SHELL_URL = Constants.protocolAndHostName + '/stickynotes/shell'
const onStickyNotesExportNotesFunctionName = 'onStickyNotesExportNotes'
const onStickyNotesFeedbackClickFunctionName = 'onStickyNotesFeedbackClick'

type ShellResponse = {
  CssUrl: string,
  JsUrl: string,
  NavBarDataJson: string,
}

function getWindowO365Shell(): IO365ShellShim {
  // tslint:disable-next-line:no-any
  const windowObj = window as any
  return windowObj.O365Shell as IO365ShellShim
}

export async function renderO365Shell(
  onSignOut: () => void,
  onExportNotesClick: () => void,
  userInfo: IAuthenticatedUserInfo
): Promise<void> {
  const response = await fetch(SHELL_URL, { credentials: 'same-origin' })

  // if we don't get a 200 then just log the issue and carry on
  if (response.status !== 200) {
    const diagnosticsInfo = getResponseDiagnostics(response)
    Log.logger.logUnexpected(Errors.shellApiError, diagnosticsInfo)
    throw logAndBuildError(diagnosticsInfo)
  }

  // if it was successful then we should parse and set up the shell
  const shellResponse: ShellResponse = await response.json()
  await initShell(shellResponse.CssUrl, shellResponse.JsUrl, shellResponse.NavBarDataJson, onSignOut, onExportNotesClick, userInfo)
}

async function initShell(
  cssUrl: string,
  jsUrl: string,
  json: string,
  onSignOut: () => void,
  exportNotesClick: () => void,
  userInfo: IAuthenticatedUserInfo
): Promise<void> {
  const o365ShellCssUrl: string = cssUrl
  const o365ShellJsUrl: string = jsUrl
  const o365ShellNavBarDataJson: string = json

  // set up script and style tags and then run the callback
  const shellStyle = document.createElement('link')
  shellStyle.href = o365ShellCssUrl
  document.body.appendChild(shellStyle)

  await addScriptToBody(o365ShellJsUrl)

  await renderShell(o365ShellNavBarDataJson, onSignOut, exportNotesClick, userInfo)
}

function onStickyNotesFeedbackClick(): void {
  window.open(Constants.DOGFOOD_TEAMS_CHAT, '_blank')
}

function getStickyNotesSettingLinks(userInfo: IAuthenticatedUserInfo, exportNotesClick: () => void): NavBarLinkData[] {
  const exportNotesLabel = LocalizationService.getLocalizedString('ExportNotes.Text')
  const feedbackLabel = LocalizationService.getLocalizedString('stickyNotes.feedback')
  const links: NavBarLinkData[] = [
    {
      Id: 'ExportNotesId',
      Text: exportNotesLabel,
      Url: `javascript:window.${onStickyNotesExportNotesFunctionName}()`,
      Action: exportNotesClick,
    },
  ]
  if (isInternalUser(userInfo.upn)) {
    links.push({
      Id: 'FeedbackId',
      Text: feedbackLabel,
      Url: `javascript:window.${onStickyNotesFeedbackClickFunctionName}()`,
      Action: onStickyNotesFeedbackClick,
    })
  }
  return links
}

function renderShell(
  data: string,
  onSignOut: () => void,
  onExportNotesClick: () => void,
  userInfo: IAuthenticatedUserInfo
): Promise<void> {
  const navBarJson = JSON.parse(data) as NavBarData
  navBarJson.SignOutLink.Url = getSignOutUrl(getForceSignInUrl(true))

  getWindowO365Shell().SetData(navBarJson)

  // tslint:disable-next-line:no-any
  const windowObj = window as any
  windowObj[onStickyNotesExportNotesFunctionName] = onExportNotesClick
  windowObj[onStickyNotesFeedbackClickFunctionName] = onStickyNotesFeedbackClick

  return new Promise((resolve: () => void, reject: (errorText: ShellLoadErrorEvent) => void) => {
    const workloadSettingsSubLinks = getStickyNotesSettingLinks(userInfo, onExportNotesClick)
    getWindowO365Shell().RenderAsync(
      {
        top: 'TopContainer',
        workloadSettingsSubLinks,
        shellDataOverrides: {
          AppHeaderLinkText: 'Sticky Notes',
          AppHeaderLinkUrl: 'https://www.onenote.com/stickynotes',
        },
      },
      () => {
        Log.logger.info('Legacy Shell rendered!')
      },
      () => {
        Log.logger.info('Legacy Shell loaded!')
        // At this point, the shell is ready to take some input in
        getWindowO365Shell().OnSignOut(onSignOut)

        resolve()
      },
      (error: ShellLoadErrorEvent) => {
        // tslint:disable-next-line:max-line-length
        Log.logger.logUnexpected(Errors.shellLoadingError, `Consumer shell failed to render! ResourceName: ${error.ResourceName} Error message: ${error.Details}`)
        reject(error)
      }
    )
  })
}
